import * as React from 'react';
import { useContext, useState } from 'react';
import { ThemeContext } from 'styled-components';
import type { DataType } from '@glueapp/graph-types';
import { getColorFromDataType } from '@/GraphEditor/helpers/getColorFromDataType';
import type { ThemeType } from '@/theme/glueTheme';

function isComplexType(dataType: DataType): boolean {
    switch (dataType.kind) {
        case 'object':
            return Object.keys(dataType.properties).length > 0;
        case 'array':
            return isComplexType(dataType.subType);
        case 'union':
            return dataType.subTypes.length > 2 || dataType.subTypes.some(isComplexType);
        case 'function':
            return true;
        case 'generic':
            return dataType.resolvedType ? isComplexType(dataType.resolvedType) : false;
        case 'optional':
            return isComplexType(dataType.innerType);
        default:
            return false;
    }
}

function getKindLabel(dataType: DataType): string {
    switch (dataType.kind) {
        case 'string':
            return 'Text';
        case 'number':
            return 'Number';
        case 'boolean':
            return 'Boolean';
        case 'stringLiteral':
            return `"${dataType.stringLiteral}"`;
        case 'numberLiteral':
            return String(dataType.numberLiteral);
        case 'null':
            return 'Null';
        case 'secret':
            return 'Secret';
        case 'object': {
            const count = Object.keys(dataType.properties).length;
            return count === 0 ? 'Object' : `Object (${count})`;
        }
        case 'array':
            return 'List';
        case 'union':
            return `Union (${dataType.subTypes.length})`;
        case 'function':
            return 'Function';
        case 'shape':
            return 'Shape';
        case 'generic':
            return dataType.identifier;
        case 'optional':
            return getKindLabel(dataType.innerType);
        default:
            return 'Unknown';
    }
}

const ChevronIcon: React.FC<{ expanded: boolean }> = ({ expanded }) => (
    <svg
        width="10"
        height="10"
        viewBox="0 0 10 10"
        fill="currentColor"
        className="transition-transform duration-150 ease-in-out"
        style={{ transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)' }}
    >
        <path d="M3 1.5L7 5L3 8.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const TypeBadge: React.FC<{ color: string; children: React.ReactNode }> = ({ color, children }) => (
    <span
        className="inline-flex items-center px-1.5 rounded text-[11px] font-medium whitespace-nowrap"
        style={{ color, backgroundColor: `${color}21`, padding: '1px 6px' }}
    >
        {children}
    </span>
);

interface TypeRendererProps {
    dataType: DataType;
    propertyName?: string;
    isOptional?: boolean;
    defaultExpanded?: boolean;
    depth?: number;
    path?: string[];
    onPropertyClick?: (dotPath: string) => void;
}

const UnpackIcon: React.FC<{ color: string }> = ({ color }) => (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
        <path d="M2 6H9M9 6L6 3M9 6L6 9" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

export const TypeRenderer: React.FC<TypeRendererProps> = ({
    dataType,
    propertyName,
    isOptional = false,
    defaultExpanded,
    depth = 0,
    path,
    onPropertyClick,
}) => {
    const shouldDefaultExpand = defaultExpanded ?? depth === 0;
    const [expanded, setExpanded] = useState(shouldDefaultExpand);
    const theme = useContext<ThemeType>(ThemeContext);
    const color = getColorFromDataType(dataType, theme);
    const displayName = dataType.name;
    const kindLabel = getKindLabel(dataType);

    if (dataType.kind === 'generic' && dataType.resolvedType) {
        return (
            <TypeRenderer
                dataType={dataType.resolvedType}
                propertyName={propertyName}
                isOptional={isOptional}
                defaultExpanded={defaultExpanded}
                depth={depth}
                path={path}
                onPropertyClick={onPropertyClick}
            />
        );
    }

    if (dataType.kind === 'optional') {
        return (
            <TypeRenderer
                dataType={dataType.innerType}
                propertyName={propertyName}
                isOptional={true}
                defaultExpanded={defaultExpanded}
                depth={depth}
                path={path}
                onPropertyClick={onPropertyClick}
            />
        );
    }

    const dotPath = path && path.length > 0 ? path.join('.') : undefined;
    const showUnpackAction = !!onPropertyClick && dotPath !== undefined;

    const renderUnpackButton = () =>
        showUnpackAction ? (
            <button
                type="button"
                className="ml-auto inline-flex items-center justify-center w-5 h-5 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white/10"
                style={{ color: theme.primaryContrastColor }}
                onPointerDown={event => event.stopPropagation()}
                onClick={event => {
                    event.stopPropagation();
                    onPropertyClick!(dotPath!);
                }}
                aria-label={`Unpack ${dotPath}`}
                title={`Unpack ${dotPath}`}
            >
                <UnpackIcon color={theme.primaryContrastColor} />
            </button>
        ) : null;

    const renderCollapsible = (children: React.ReactNode) => {
        const hasChildren = React.Children.count(children) > 0;
        const toggleExpanded = hasChildren ? () => setExpanded(prev => !prev) : undefined;
        return (
            <div>
                <div
                    className="group flex items-center gap-1 py-px leading-snug"
                    style={{ cursor: hasChildren ? 'pointer' : undefined }}
                    onClick={toggleExpanded}
                >
                    {hasChildren ? (
                        <span
                            className="inline-flex items-center justify-center w-4 h-4 mr-0.5 rounded-sm hover:bg-white/10"
                            style={{ color: theme.subtleContrastColor }}
                            aria-label={expanded ? 'Collapse' : 'Expand'}
                        >
                            <ChevronIcon expanded={expanded} />
                        </span>
                    ) : depth > 0 ? (
                        <span className="inline-block w-[18px]" />
                    ) : null}
                    {propertyName !== undefined && (
                        <span className="text-[11px] mr-1" style={{ color: theme.subtleContrastColor }}>
                            {propertyName}:
                        </span>
                    )}
                    {displayName && <span className="text-[11px] font-medium">{displayName}</span>}
                    <TypeBadge color={color}>{kindLabel}</TypeBadge>
                    {isOptional && (
                        <span className="text-[10px] italic" style={{ color: theme.disabledText }}>
                            ?
                        </span>
                    )}
                    {renderUnpackButton()}
                </div>
                {expanded && hasChildren && (
                    <div className="ml-3 pl-2 mt-0.5" style={{ borderLeft: `1px solid ${theme.separatorColor}` }}>
                        {children}
                    </div>
                )}
            </div>
        );
    };

    const renderSimple = () => (
        <div className="group flex items-center gap-1 py-px leading-snug">
            {depth > 0 && <span className="inline-block w-[18px]" />}
            {propertyName !== undefined && (
                <span className="text-[11px] mr-1" style={{ color: theme.subtleContrastColor }}>
                    {propertyName}:
                </span>
            )}
            {displayName && <span className="text-[11px] font-medium">{displayName}</span>}
            <TypeBadge color={color}>{kindLabel}</TypeBadge>
            {isOptional && (
                <span className="text-[10px] italic" style={{ color: theme.disabledText }}>
                    ?
                </span>
            )}
            {renderUnpackButton()}
        </div>
    );

    switch (dataType.kind) {
        case 'string':
        case 'number':
        case 'boolean':
        case 'stringLiteral':
        case 'numberLiteral':
        case 'null':
        case 'secret':
            return renderSimple();

        case 'object': {
            const entries = Object.entries(dataType.properties);
            if (entries.length === 0) {
                return renderCollapsible(
                    <span className="text-[11px] italic" style={{ color: theme.disabledText }}>
                        (empty)
                    </span>,
                );
            }
            return renderCollapsible(
                <>
                    {entries.map(([key, propType]) => (
                        <TypeRenderer
                            key={key}
                            dataType={propType}
                            propertyName={key}
                            isOptional={dataType.optionalProperties?.includes(key)}
                            depth={depth + 1}
                            path={[...(path ?? []), key]}
                            onPropertyClick={onPropertyClick}
                        />
                    ))}
                </>,
            );
        }

        case 'array': {
            if (isComplexType(dataType.subType)) {
                return renderCollapsible(
                    <TypeRenderer dataType={dataType.subType} depth={depth + 1} path={path} onPropertyClick={onPropertyClick} />,
                );
            }
            return (
                <div className="group flex items-center gap-1 py-px leading-snug">
                    {depth > 0 && <span className="inline-block w-[18px]" />}
                    {propertyName !== undefined && (
                        <span className="text-[11px] mr-1" style={{ color: theme.subtleContrastColor }}>
                            {propertyName}:
                        </span>
                    )}
                    <TypeBadge color={color}>{kindLabel}</TypeBadge>
                    <span className="text-[11px]">of</span>
                    <TypeBadge color={getColorFromDataType(dataType.subType, theme)}>
                        {dataType.subType.name || getKindLabel(dataType.subType)}
                    </TypeBadge>
                    {isOptional && (
                        <span className="text-[10px] italic" style={{ color: theme.disabledText }}>
                            ?
                        </span>
                    )}
                    {renderUnpackButton()}
                </div>
            );
        }

        case 'union': {
            const shouldCollapse = dataType.subTypes.length > 2 || dataType.subTypes.some(isComplexType);
            if (shouldCollapse) {
                return renderCollapsible(
                    <>
                        {dataType.subTypes.map((subType: DataType, index: number) => (
                            <TypeRenderer key={index} dataType={subType} depth={depth + 1} />
                        ))}
                    </>,
                );
            }
            return (
                <div className="group flex items-center gap-1 py-px leading-snug">
                    {depth > 0 && <span className="inline-block w-[18px]" />}
                    {propertyName !== undefined && (
                        <span className="text-[11px] mr-1" style={{ color: theme.subtleContrastColor }}>
                            {propertyName}:
                        </span>
                    )}
                    {dataType.subTypes.map((subType: DataType, index: number) => (
                        <React.Fragment key={index}>
                            {index > 0 && <span className="text-[10px] text-gray-500">|</span>}
                            <TypeBadge color={getColorFromDataType(subType, theme)}>{subType.name || getKindLabel(subType)}</TypeBadge>
                        </React.Fragment>
                    ))}
                    {isOptional && (
                        <span className="text-[10px] italic" style={{ color: theme.disabledText }}>
                            ?
                        </span>
                    )}
                    {renderUnpackButton()}
                </div>
            );
        }

        case 'function': {
            const inputEntries = Object.entries(dataType.inputTypes);
            const outputEntries = Object.entries(dataType.outputTypes);
            return renderCollapsible(
                <>
                    {inputEntries.length > 0 && (
                        <>
                            <div className="text-[10px] uppercase tracking-wide mt-1 mb-0.5" style={{ color: theme.disabledText }}>
                                Inputs
                            </div>
                            {inputEntries.map(([key, inputType]) => (
                                <TypeRenderer key={key} dataType={inputType} propertyName={key} depth={depth + 1} />
                            ))}
                        </>
                    )}
                    {outputEntries.length > 0 && (
                        <>
                            <div className="text-[10px] uppercase tracking-wide mt-1 mb-0.5" style={{ color: theme.disabledText }}>
                                Outputs
                            </div>
                            {outputEntries.map(([key, outputType]) => (
                                <TypeRenderer key={key} dataType={outputType} propertyName={key} depth={depth + 1} />
                            ))}
                        </>
                    )}
                </>,
            );
        }

        case 'generic': {
            if (dataType.extends) {
                return renderCollapsible(<TypeRenderer dataType={dataType.extends} depth={depth + 1} />);
            }
            return renderSimple();
        }

        case 'shape': {
            if (dataType.restriction) {
                return renderCollapsible(<TypeRenderer dataType={dataType.restriction} depth={depth + 1} />);
            }
            return renderSimple();
        }
    }
};
